import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Box, Button, Flex, HStack, InputGroup, Text } from "@chakra-ui/react";
import { USER_REQUEST_API } from "constants/endpoints";
import useApi from "hooks/useApi";

import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { showToast } from "components/toast/Toast";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { ENTITY_FILTER_TYPES } from "utils/common";
import { getFilterFromLocalStorage } from "utils/common";
import { setFilterToLocalStorage } from "utils/common";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import { ENTITY_COLUMNS_TYPES } from "utils/common";
import CustomTable from "components/table/CustomTable";
import { userRequestColumns } from "./variables";
import { formatUsersRequestTableData } from "utils/common";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import { USER_REQUEST_STATUS } from "constants/filterDropdownData";
import { USER_REQUEST_TYPES } from "constants/filterDropdownData";
import { USER_REQUEST_SOURCE } from "constants/filterDropdownData";
import Menu from "components/menu/MainMenu";
import MultiSelectMenu from "components/menu/MultiSelectMenu";
import { UPDATE_USER_REQUEST } from "constants/endpoints";
import ShareToWhatsapp from "../users/components/modal/ShareToWhatsapp";
import { SearchBar } from "components/navbar/searchBar/SearchBar";

const PAGE_SIZE = 50;
const today = new Date();
const tenDaysAgo = new Date();
tenDaysAgo.setDate(today.getDate() - 10);

export default function UserRequests() {
  const { apiCall, loading } = useApi();
  const [userData, setUserData] = useState([]);
  const [apiError, setApiError] = useState(null);
  const lsFiltersUsersPageSize = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.REQUEST_PAGE_SIZE
  );
  const [pageSize, setPageSize] = useState(
    lsFiltersUsersPageSize && Number.isInteger(lsFiltersUsersPageSize)
      ? lsFiltersUsersPageSize
      : PAGE_SIZE
  );
  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [isShareToWhatsappOpen, setIsShareToWhatsappOpen] =useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchData = useCallback(async (providedUserText) => {
    const validPageSize = Number.isInteger(pageSize) ? pageSize : PAGE_SIZE;
    try {
      const response = await apiCall.get(`${USER_REQUEST_API}`, {
        ...filters,
        type: filters?.type?.join(","),
        page: currentPage,
        limit: validPageSize,
        search_text: providedUserText || searchText || "",
      });

      const rawData = response?.data?.requests || [];
      const formattedData = formatUsersRequestTableData(rawData);

      setTotalPages(Math.ceil(response?.data?.count / pageSize));
      setTotalCount(response?.data?.count);
      setUserData(formattedData);
    } catch (error) {
      console.error("Error fetching user requests:", error);
      setApiError(error);
    }
  }, [currentPage, pageSize, filters, searchText]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const columns = useMemo(() => userRequestColumns, [userRequestColumns]);

  const lsLocalStorageManageColumns = getFilterFromLocalStorage(
    ENTITY_COLUMNS_TYPES.REQUESTS_COLUMNS
  );

  const [columnVisibility, setColumnVisibility] = useState(
    lsLocalStorageManageColumns
      ? lsLocalStorageManageColumns
      : columns.reduce((acc, column) => {
          acc[column.accessor] = true;
          return acc;
        }, {})
  );

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  useEffect(() => {
    setFilterToLocalStorage(
      ENTITY_COLUMNS_TYPES.REQUESTS_COLUMNS,
      columnVisibility
    );
  }, [columnVisibility]);

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor]
  );

  const dropdownFilters = [
    {
      name: "status",
      placeholder: "Select Status",
      value: filters?.status,
      data: USER_REQUEST_STATUS,
    },
    {
      name: "type",
      placeholder: "Request Type",
      value: filters?.type,
      data: USER_REQUEST_TYPES,
    },
  ];

  const allFiltersProps = {
    dropdowns: dropdownFilters,
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchData();
  };

  const handleRemoveAllFilter = () => {
    setFilters({});
  };

  const setStartDate = (text) => {
    setFilters((prev) => {
      return { ...prev, start_date: text };
    });
  };

  const setEndDate = (text) => {
    setFilters((prev) => {
      return { ...prev, end_date: text };
    });
  };

  const updateRequestStatus = useCallback(async (ids, status, comment, updatedPropertyType) => {
    try {
      const payload = {
        ids,
        status,
        comment
      }
      if(status === "Add Comment"){
        if(comment === selectedAction.comment) return;
        delete payload.status;
      }
      if(updatedPropertyType === 'Share To Whatsapp'){
        setIsShareToWhatsappOpen(true);
        return;
      }
      
      await apiCall.patch(UPDATE_USER_REQUEST, payload);
      await fetchData();
      showToast({
        message: "Status updated successfully",
        customStyle: {
          background: "green",
          color: "#fff",
          padding: "10px",
        },
      });
    } catch (error) {
      console.error("Error fetching user requests:", error);
      showToast({
        message: error.response.data.error.message || error.response.data.error || error.message,
        customStyle: {
          background: "red",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  }, [selectedAction]);

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find(
        (item) => item._id === row._id
      );

      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item._id !== findData._id);
        } else {
          return [...prev, row];
        }
      });
    },
    [selectedRowsData]
  );

  const handleInputFormSubmit = (e) => {
    e.preventDefault();

    setCurrentPage(1);
    fetchData(searchText);
  };

  if (apiError) {
    return <ErrorDisplay error={apiError} />;
  }

  return (
    <>
      <Box height="100vh" bg="#F8FAFB" mr={14} pr={6}>
        <Flex
          justify="space-between"
          align="center"
          p="2"
          boxShadow="sm"
          borderRadius="sm"
          pos="fixed"
          style={{ width: "calc(100vw - 96px)" }}
          zIndex={1000}
        >
          <HStack spacing="4" p={2}>
            <Box position="relative" display="inline-block">
              <Button
                paddingX={6}
                leftIcon={<FiFilter />}
                rightIcon={<FiChevronsDown />}
                colorScheme="teal"
                variant="outline"
                borderRadius="md"
                size="sm"
                onClick={() => {
                  setIsOpenFilterSidebar(!isOpenFilterSidebar);
                }}
              >
                Filter
              </Button>
            </Box>
            <ManageColumns
              columns={columns}
              handleColumnChange={handleColumnChange}
              columnVisibility={columnVisibility}
              setManageColumnSelectAllCheckbox={
                setManageColumnSelectAllCheckbox
              }
              manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
            />
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </HStack>
          <Text
            borderRadius="5px"
            borderWidth="0.5px"
            borderColor="#637381"
            background="#F9F9FB"
            color="#637381"
            p={1}
          >
            Total User Requests: {totalCount}
          </Text>
          *{/* Pagination - Desktop View */}
          <Box display={{ base: "none", md: "block" }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(value) => setCurrentPage(value)}
              totalCount={totalCount}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              setPageSizeToLocalStorage={(value) =>
                setFilterToLocalStorage(
                ENTITY_FILTER_TYPES.USERS_PAGE_SIZE,
                value
              )
            }
          />
      </Box>

        </Flex>
        <SidebarFilter
          filters={allFiltersProps}
          handleFilterChange={handleFilterChange}
          handleApplyFilter={handleApplyFilter}
          isOpen={isOpenFilterSidebar}
          onClose={() => setIsOpenFilterSidebar(!isOpenFilterSidebar)}
          appliedFilters={filters}
          setFilters={setFilters}
          handleRemoveAllFilter={handleRemoveAllFilter}
          isCalenderFilter={true}
          startDate={filters?.start_date}
          setStartDate={setStartDate}
          endDate={filters?.end_date}
          setEndDate={setEndDate}
          enableMultiselect={true}
          multiSelectKey={["type"]}
        />
        {userData?.length !== 0 ? (
          <>
            <CustomTable
              columns={visibleColumns}
              data={userData}
              showSelect={false}
              onSelectedAction={setSelectedAction}
              setSelectedRowsData={setSelectedRowsData}
              handleRowSelect={handleRowSelect}
              menuComponent={
                <Menu
                  onUpdatePropertyStatus={({updatedPropertyType,id, status, comment}) => {
                    updateRequestStatus([id], status, comment, updatedPropertyType);
                  }}
                  currentValue={selectedAction.status}
                  selectedAction={selectedAction}
                  dataType={"user_requests"}
                  showInput={true}
                />
              }
            />
          </>
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        {/* Pagination - Mobile View */}
        <Box
          display={{ base: "block", md: "none" }}
          position="fixed"
          bottom="0"
          left="0"
          width="100%"
          bg="white"
          boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
          zIndex="1000"
          p={2}
        >
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => setCurrentPage(value)}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageSizeToLocalStorage={(value) =>
            setFilterToLocalStorage(
              ENTITY_FILTER_TYPES.USERS_PAGE_SIZE,
              value
            )
          }
        />
      </Box>

        <OverlayLoaderComponent isOpen={loading} />
      </Box>
      {isShareToWhatsappOpen && (
        <ShareToWhatsapp isOpen={isShareToWhatsappOpen} onClose={()=>setIsShareToWhatsappOpen(false)} userData={selectedAction?.user_data} defaultMessageType={'post_property'} />
      )}
    </>
  );
}
